import React, { useState, useEffect } from 'react';
import {
  Key,
  Eye,
  EyeOff,
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Settings,
  Shield,
  Bot,
  Lock
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface APIKeyConfig {
  chatgpt: {
    configured: boolean;
    keyPreview: string | null;
    model: string;
    maxTokens: number;
    temperature: number;
  };
  okta: {
    configured: boolean;
    domain: string | null;
    clientIdPreview: string | null;
    samlCertConfigured: boolean;
  };
  anthropic: {
    configured: boolean;
    keyPreview: string | null;
    model: string;
    maxTokens: number;
  };
  gemini: {
    configured: boolean;
    keyPreview: string | null;
    model: string;
    maxTokens: number;
    temperature: number;
  };
  mistral: {
    configured: boolean;
    keyPreview: string | null;
    model: string;
    maxTokens: number;
    temperature: number;
  };
}

interface SystemStatus {
  chatgptAvailable: boolean;
  oktaAvailable: boolean;
  anthropicAvailable: boolean;
  geminiAvailable: boolean;
  mistralAvailable: boolean;
}

interface FormData {
  provider: string;
  configuration: {
    apiKey?: string;
    model?: string;
    maxTokens?: number;
    temperature?: number;
    domain?: string;
    clientId?: string;
    samlCert?: string;
    autoProvision?: boolean;
  };
}

const APIKeysConfig: React.FC = () => {
  const [config, setConfig] = useState<APIKeyConfig | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Fetch API keys configuration
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/config/api-keys');
      const data = response.data;
      
      setConfig(data.apiKeys);
      setSystemStatus(data.systemStatus);
      setLastUpdated(data.lastUpdated);
      
      // Initialize form data with current config
      setFormData({
        chatgpt: {
          model: data.apiKeys.chatgpt.model,
          maxTokens: data.apiKeys.chatgpt.maxTokens,
          temperature: data.apiKeys.chatgpt.temperature,
          apiKey: ''
        },
        okta: {
          domain: data.apiKeys.okta.domain || '',
          clientId: '',
          samlCert: '',
          autoProvision: false
        },
        anthropic: {
          model: data.apiKeys.anthropic.model,
          maxTokens: data.apiKeys.anthropic.maxTokens,
          apiKey: ''
        },
        gemini: {
          model: data.apiKeys.gemini?.model || 'gemini-1.5-pro',
          maxTokens: data.apiKeys.gemini?.maxTokens || 8000,
          temperature: data.apiKeys.gemini?.temperature || 0.7,
          apiKey: ''
        },
        mistral: {
          model: data.apiKeys.mistral?.model || 'mistral-large-latest',
          maxTokens: data.apiKeys.mistral?.maxTokens || 4000,
          temperature: data.apiKeys.mistral?.temperature || 0.7,
          apiKey: ''
        }
      });
    } catch (error: any) {
      console.error('Error fetching API keys config:', error);
      toast.error('Failed to load API keys configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Save configuration
  const saveConfiguration = async (provider: string) => {
    try {
      setSaving(provider);
      
      const payload = {
        provider,
        configuration: formData[provider]
      };

      const response = await api.put('/api/admin/config/api-keys', payload);
      
      toast.success(response.data.message || `${provider.toUpperCase()} configuration saved successfully`);
      
      // Refresh config
      await fetchConfig();
      
      // Clear sensitive fields
      setFormData(prev => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          apiKey: '',
          clientId: '',
          samlCert: ''
        }
      }));
      
    } catch (error: any) {
      console.error(`Error saving ${provider} config:`, error);
      toast.error(error.response?.data?.error || `Failed to save ${provider} configuration`);
    } finally {
      setSaving(null);
    }
  };

  // Test API key
  const testAPIKey = async (provider: string) => {
    try {
      setTesting(provider);
      
      // Prepare test data based on provider
      let testData: any = { provider };
      
      if (provider === 'chatgpt' || provider === 'anthropic' || provider === 'gemini' || provider === 'mistral') {
        testData.testKey = formData[provider]?.apiKey;
      } else if (provider === 'okta') {
        testData = {
          ...testData,
          domain: formData[provider]?.domain,
          testKey: formData[provider]?.clientId,
          clientId: formData[provider]?.clientId,
          samlCert: formData[provider]?.samlCert
        };
      }
      
      const response = await api.post('/api/admin/config/api-keys/test', testData);
      
      const result = response.data;
      
      if (result.status === 'success') {
        toast.success(`✅ ${result.message}`, {
          duration: 4000,
        });
        if (result.details) {
          console.log(`${provider} test details:`, result.details);
        }
      } else if (result.status === 'warning') {
        toast(`⚠️ ${result.message}`, {
          duration: 5000,
          style: {
            background: '#f59e0b',
            color: 'white',
          },
        });
        if (result.details) {
          console.log(`${provider} test warning:`, result.details);
        }
      } else {
        toast.error(`❌ ${result.message}`, {
          duration: 4000,
        });
        if (result.details) {
          console.log(`${provider} test error:`, result.details);
        }
      }
      
    } catch (error: any) {
      console.error(`Error testing ${provider} key:`, error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || `Failed to test ${provider} configuration`;
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setTesting(null);
    }
  };

  // Toggle key visibility
  const toggleKeyVisibility = (provider: string) => {
    setShowKeys(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }));
  };

  // Update form data
  const updateFormData = (provider: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  // Get status icon
  const getStatusIcon = (available: boolean) => {
    return available ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading API keys configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Key className="w-8 h-8 text-blue-600" />
            API Keys Configuration
          </h2>
          <p className="text-gray-600">
            Configure system-wide API keys for external services
          </p>
          {lastUpdated && (
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={fetchConfig}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* System Status Overview */}
      <div className="bg-white rounded-lg shadow border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          System Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="w-6 h-6 text-blue-600" />
              <span className="font-medium">ChatGPT</span>
            </div>
            {getStatusIcon(systemStatus?.chatgptAvailable || false)}
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-green-600" />
              <span className="font-medium">Okta SAML</span>
            </div>
            {getStatusIcon(systemStatus?.oktaAvailable || false)}
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="w-6 h-6 text-purple-600" />
              <span className="font-medium">Anthropic</span>
            </div>
            {getStatusIcon(systemStatus?.anthropicAvailable || false)}
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="w-6 h-6 text-red-600" />
              <span className="font-medium">Gemini</span>
            </div>
            {getStatusIcon(systemStatus?.geminiAvailable || false)}
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="w-6 h-6 text-orange-600" />
              <span className="font-medium">Mistral</span>
            </div>
            {getStatusIcon(systemStatus?.mistralAvailable || false)}
          </div>
        </div>
      </div>

      {/* ChatGPT Configuration */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bot className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold">ChatGPT / OpenAI Configuration</h3>
          {config?.chatgpt.configured && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showKeys.chatgpt ? 'text' : 'password'}
                  value={formData.chatgpt?.apiKey || ''}
                  onChange={(e) => updateFormData('chatgpt', 'apiKey', e.target.value)}
                  placeholder={config?.chatgpt.configured ? 'Enter new API key' : 'sk-...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => toggleKeyVisibility('chatgpt')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showKeys.chatgpt ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={() => testAPIKey('chatgpt')}
                disabled={testing === 'chatgpt' || !formData.chatgpt?.apiKey}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Test API Key"
              >
                <TestTube className={`w-4 h-4 ${testing === 'chatgpt' ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {config?.chatgpt.keyPreview && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.chatgpt.keyPreview}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={formData.chatgpt?.model || 'gpt-4'}
              onChange={(e) => updateFormData('chatgpt', 'model', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={formData.chatgpt?.maxTokens || 4000}
              onChange={(e) => updateFormData('chatgpt', 'maxTokens', parseInt(e.target.value))}
              min="1"
              max="32000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.chatgpt?.temperature || 0.7}
              onChange={(e) => updateFormData('chatgpt', 'temperature', parseFloat(e.target.value))}
              min="0"
              max="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => saveConfiguration('chatgpt')}
            disabled={saving === 'chatgpt'}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving === 'chatgpt' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save ChatGPT Config
          </button>
        </div>
      </div>

      {/* Okta SAML Configuration */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold">Okta SAML Configuration</h3>
          {config?.okta.configured && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Okta Domain
            </label>
            <input
              type="text"
              value={formData.okta?.domain || ''}
              onChange={(e) => updateFormData('okta', 'domain', e.target.value)}
              placeholder="your-domain.okta.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {config?.okta.domain && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.okta.domain}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client ID
            </label>
            <div className="flex gap-2">
              <input
                type={showKeys.okta ? 'text' : 'password'}
                value={formData.okta?.clientId || ''}
                onChange={(e) => updateFormData('okta', 'clientId', e.target.value)}
                placeholder="0oaXXXXXXXXXXXXXXXX"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                type="button"
                onClick={() => toggleKeyVisibility('okta')}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {showKeys.okta ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {config?.okta.clientIdPreview && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.okta.clientIdPreview}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SAML Certificate
            </label>
            <textarea
              value={formData.okta?.samlCert || ''}
              onChange={(e) => updateFormData('okta', 'samlCert', e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {config?.okta.samlCertConfigured && (
              <p className="text-xs text-gray-500 mt-1">
                ✓ SAML Certificate is configured
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.okta?.autoProvision || false}
                onChange={(e) => updateFormData('okta', 'autoProvision', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                Auto-provision users from Okta
              </span>
            </label>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => testAPIKey('okta')}
            disabled={testing === 'okta'}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <TestTube className={`w-4 h-4 ${testing === 'okta' ? 'animate-spin' : ''}`} />
            Test Configuration
          </button>
          <button
            onClick={() => saveConfiguration('okta')}
            disabled={saving === 'okta'}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {saving === 'okta' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Okta Config
          </button>
        </div>
      </div>

      {/* Anthropic Configuration */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bot className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold">Anthropic Claude Configuration</h3>
          {config?.anthropic.configured && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="flex gap-2">
              <input
                type={showKeys.anthropic ? 'text' : 'password'}
                value={formData.anthropic?.apiKey || ''}
                onChange={(e) => updateFormData('anthropic', 'apiKey', e.target.value)}
                placeholder={config?.anthropic.configured ? 'Enter new API key' : 'sk-ant-...'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                type="button"
                onClick={() => toggleKeyVisibility('anthropic')}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {showKeys.anthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => testAPIKey('anthropic')}
                disabled={testing === 'anthropic' || !formData.anthropic?.apiKey}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Test API Key"
              >
                <TestTube className={`w-4 h-4 ${testing === 'anthropic' ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {config?.anthropic.keyPreview && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.anthropic.keyPreview}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={formData.anthropic?.model || 'claude-3-sonnet-20240229'}
              onChange={(e) => updateFormData('anthropic', 'model', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="claude-3-opus-20240229">Claude 3 Opus</option>
              <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
              <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={formData.anthropic?.maxTokens || 4000}
              onChange={(e) => updateFormData('anthropic', 'maxTokens', parseInt(e.target.value))}
              min="1"
              max="200000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => saveConfiguration('anthropic')}
            disabled={saving === 'anthropic'}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {saving === 'anthropic' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Anthropic Config
          </button>
        </div>
      </div>

      {/* Gemini Configuration */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bot className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-semibold">Google Gemini Configuration</h3>
          {config?.gemini?.configured && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="flex gap-2">
              <input
                type={showKeys.gemini ? 'text' : 'password'}
                value={formData.gemini?.apiKey || ''}
                onChange={(e) => updateFormData('gemini', 'apiKey', e.target.value)}
                placeholder={config?.gemini?.configured ? 'Enter new API key' : 'AIza...'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <button
                type="button"
                onClick={() => toggleKeyVisibility('gemini')}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {showKeys.gemini ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => testAPIKey('gemini')}
                disabled={testing === 'gemini' || !formData.gemini?.apiKey}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Test API Key"
              >
                <TestTube className={`w-4 h-4 ${testing === 'gemini' ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {config?.gemini?.keyPreview && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.gemini.keyPreview}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={formData.gemini?.model || 'gemini-1.5-pro'}
              onChange={(e) => updateFormData('gemini', 'model', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
              <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
              <option value="gemini-1.0-pro">Gemini 1.0 Pro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={formData.gemini?.maxTokens || 8000}
              onChange={(e) => updateFormData('gemini', 'maxTokens', parseInt(e.target.value))}
              min="1"
              max="32000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.gemini?.temperature || 0.7}
              onChange={(e) => updateFormData('gemini', 'temperature', parseFloat(e.target.value))}
              min="0"
              max="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => saveConfiguration('gemini')}
            disabled={saving === 'gemini'}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {saving === 'gemini' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Gemini Config
          </button>
        </div>
      </div>

      {/* Mistral Configuration */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bot className="w-6 h-6 text-orange-600" />
          <h3 className="text-lg font-semibold">Mistral AI Configuration</h3>
          {config?.mistral?.configured && (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              Configured
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="flex gap-2">
              <input
                type={showKeys.mistral ? 'text' : 'password'}
                value={formData.mistral?.apiKey || ''}
                onChange={(e) => updateFormData('mistral', 'apiKey', e.target.value)}
                placeholder={config?.mistral?.configured ? 'Enter new API key' : 'mistral...'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <button
                type="button"
                onClick={() => toggleKeyVisibility('mistral')}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {showKeys.mistral ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => testAPIKey('mistral')}
                disabled={testing === 'mistral' || !formData.mistral?.apiKey}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Test API Key"
              >
                <TestTube className={`w-4 h-4 ${testing === 'mistral' ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {config?.mistral?.keyPreview && (
              <p className="text-xs text-gray-500 mt-1">
                Current: {config.mistral.keyPreview}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={formData.mistral?.model || 'mistral-large-latest'}
              onChange={(e) => updateFormData('mistral', 'model', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="mistral-large-latest">Mistral Large</option>
              <option value="mistral-medium-latest">Mistral Medium</option>
              <option value="mistral-small-latest">Mistral Small</option>
              <option value="mistral-tiny">Mistral Tiny</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={formData.mistral?.maxTokens || 4000}
              onChange={(e) => updateFormData('mistral', 'maxTokens', parseInt(e.target.value))}
              min="1"
              max="32000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.mistral?.temperature || 0.7}
              onChange={(e) => updateFormData('mistral', 'temperature', parseFloat(e.target.value))}
              min="0"
              max="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => saveConfiguration('mistral')}
            disabled={saving === 'mistral'}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
          >
            {saving === 'mistral' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Mistral Config
          </button>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
            <p className="text-sm text-yellow-700 mt-1">
              API keys are securely encrypted and stored. Only administrators can view and modify these configurations.
              Test your configurations after making changes to ensure they work correctly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIKeysConfig;